import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchRawThematics } from "../../HTTP/thematics";
import { addNode, updateNode, reorderNodes, deleteNode } from "../../HTTP/thematicsNodes";

const bySiblingOrder = (a, b) => (a.position ?? 0) - (b.position ?? 0) || String(a.code).localeCompare(String(b.code))

export default function ThematicsTreeAdmin() {
    const localUser = JSON.parse(localStorage.getItem("user"))
    if (!localUser.super_admin && localUser.admin_access !== 6) {
        window.location.href = '/'
    }

    const [nodes, setNodes] = useState([])
    const [error, setError] = useState("")
    const [collapsed, setCollapsed] = useState({})

    const load = useCallback(async () => {
        const res = await fetchRawThematics()
        setNodes(Array.isArray(res) ? res : [])
    }, [])

    useEffect(() => { load() }, [load])

    const childrenByParent = useMemo(() => {
        const map = {}
        nodes.forEach((n) => {
            const key = n.parent_id ?? "root"
            ;(map[key] = map[key] || []).push(n)
        })
        Object.values(map).forEach((list) => list.sort(bySiblingOrder))
        return map
    }, [nodes])

    const run = async (action) => {
        setError("")
        const res = await action()
        if (!res.ok) {
            setError(res.message)
            return
        }
        await load()
    }

    const handleAdd = (parent) => {
        const name = window.prompt(parent ? `Nouveau nœud sous « ${parent.name} »` : "Nom du nouveau thème")
        if (!name || !name.trim()) return
        run(() => addNode({ parent_id: parent ? parent.id : null, name: name.trim() }))
    }

    const handleRename = (node) => {
        const name = window.prompt("Nouveau nom", node.name)
        if (!name || !name.trim() || name.trim() === node.name) return
        run(() => updateNode({ id: node.id, name: name.trim() }))
    }

    const handleMove = (node) => {
        const value = window.prompt("Id du nouveau parent (vide = niveau racine)", node.parent_id ?? "")
        if (value === null) return
        run(() => updateNode({ id: node.id, parent_id: value.trim() === "" ? null : Number(value) }))
    }

    const handleToggleActive = (node) => {
        const active = node.is_active === 0 || node.is_active === false
        run(() => updateNode({ id: node.id, is_active: active ? 1 : 0 }))
    }

    const handleReorder = (node, direction) => {
        const siblings = childrenByParent[node.parent_id ?? "root"] || []
        const index = siblings.findIndex((s) => s.id === node.id)
        const target = index + direction
        if (target < 0 || target >= siblings.length) return
        const ids = siblings.map((s) => s.id)
        ;[ids[index], ids[target]] = [ids[target], ids[index]]
        run(() => reorderNodes(node.parent_id ?? null, ids))
    }

    const handleDelete = (node) => {
        if (!window.confirm(`Supprimer « ${node.name} » ?`)) return
        run(() => deleteNode(node.id))
    }

    const renderNode = (node, depth) => {
        const children = childrenByParent[node.id] || []
        const inactive = node.is_active === 0 || node.is_active === false
        const isCollapsed = !!collapsed[node.id]
        return (
            <div key={node.id}>
                <div className="flex items-center gap-2 py-1 border-b border-gray-100" style={{ paddingLeft: depth * 20 }}>
                    <button
                        className="w-5 text-gray-500"
                        onClick={() => setCollapsed((prev) => ({ ...prev, [node.id]: !prev[node.id] }))}
                    >
                        {children.length > 0 ? (isCollapsed ? "▸" : "▾") : ""}
                    </button>
                    <span className="text-xs text-gray-400">{node.code}</span>
                    <span className={inactive ? "line-through text-gray-400" : ""}>{node.name}</span>
                    <span className="ml-auto flex gap-1 text-xs">
                        <button className="px-1 border rounded" onClick={() => handleReorder(node, -1)}>↑</button>
                        <button className="px-1 border rounded" onClick={() => handleReorder(node, 1)}>↓</button>
                        <button className="px-1 border rounded" onClick={() => handleAdd(node)}>+ enfant</button>
                        <button className="px-1 border rounded" onClick={() => handleRename(node)}>Renommer</button>
                        <button className="px-1 border rounded" onClick={() => handleMove(node)}>Déplacer</button>
                        <button className="px-1 border rounded" onClick={() => handleToggleActive(node)}>{inactive ? "Activer" : "Désactiver"}</button>
                        <button className="px-1 border rounded text-red-600" onClick={() => handleDelete(node)}>Supprimer</button>
                    </span>
                </div>
                {!isCollapsed && children.map((child) => renderNode(child, depth + 1))}
            </div>
        )
    }

    return (
        <div className="max-w-5xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Arbre des thématiques</h2>
                <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded" onClick={() => handleAdd(null)}>
                    Ajouter un thème
                </button>
            </div>
            {error && <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">{error}</div>}
            {(childrenByParent["root"] || []).map((node) => renderNode(node, 0))}
        </div>
    )
}
